'use client';

import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { cn } from '@glomopay/utils';

type AnchorRegistry = {
  anchor: HTMLElement | null;
  setAnchor: (node: HTMLElement | null) => void;
  setTrigger: (node: HTMLElement | null) => void;
};

const AnchorRegistryContext = React.createContext<AnchorRegistry | null>(null);

function Popover(props: React.ComponentProps<typeof PopoverPrimitive.Root>) {
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  const [trigger, setTrigger] = React.useState<HTMLElement | null>(null);
  const registry = React.useMemo(() => ({ anchor: anchor ?? trigger, setAnchor, setTrigger }), [anchor, trigger]);

  return (
    <AnchorRegistryContext.Provider value={registry}>
      <PopoverPrimitive.Root {...props} />
    </AnchorRegistryContext.Provider>
  );
}

function useRegisteredRef<T extends HTMLElement>(forwardedRef: React.ForwardedRef<T>, register?: (node: HTMLElement | null) => void) {
  return React.useCallback(
    (node: T | null) => {
      register?.(node);
      if (typeof forwardedRef === 'function') forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    },
    [forwardedRef, register],
  );
}

const PopoverTrigger = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Trigger>
>((props, ref) => {
  const registry = React.useContext(AnchorRegistryContext);
  return <PopoverPrimitive.Trigger ref={useRegisteredRef(ref, registry?.setTrigger)} {...props} />;
});
PopoverTrigger.displayName = PopoverPrimitive.Trigger.displayName;

const PopoverAnchor = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Anchor>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Anchor>
>((props, ref) => {
  const registry = React.useContext(AnchorRegistryContext);
  return <PopoverPrimitive.Anchor ref={useRegisteredRef(ref, registry?.setAnchor)} {...props} />;
});
PopoverAnchor.displayName = PopoverPrimitive.Anchor.displayName;

const PopoverClose = PopoverPrimitive.Close;

// Radix's hideWhenDetached only checks the viewport, not scroll containers, so an open popover kept
// following an anchor that had scrolled out of a dialog body and rendered outside the dialog.
// IntersectionObserver applies every ancestor's overflow clip.
function useIsInView(element: HTMLElement | null) {
  const [isInView, setIsInView] = React.useState(true);

  React.useEffect(() => {
    if (!element || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(([entry]) => setIsInView(entry.isIntersecting));
    observer.observe(element);
    return () => {
      observer.disconnect();
      setIsInView(true);
    };
  }, [element]);

  return isInView;
}

// z-1350 clears MUI's modal layer (1300): merchant-dashboard still opens shadcn popovers
// inside MUI Dialogs, and at z-50 the popover renders beneath the dialog surface, which
// swallows every click on it.
const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = 'center', sideOffset = 4, onWheel, onTouchMove, ...props }, ref) => {
  const isAnchorInView = useIsInView(React.useContext(AnchorRegistryContext)?.anchor ?? null);

  return (
    <PopoverPrimitive.Portal>
      {/* A modal Dialog's scroll lock cancels wheel/touch events outside its DOM, and this content is
          portalled out of it, so a scrollable popover opened from a dialog would not scroll. */}
      <PopoverPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        onWheel={(event) => {
          event.stopPropagation();
          onWheel?.(event);
        }}
        onTouchMove={(event) => {
          event.stopPropagation();
          onTouchMove?.(event);
        }}
        className={cn(
          'bg-bg-elevated text-text-primary data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-[1350] w-72 origin-[--radix-popover-content-transform-origin] rounded-md border p-4 shadow-md outline-none',
          !isAnchorInView && 'pointer-events-none invisible',
          className,
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
});
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor, PopoverClose };
